import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["selectedChannelId", "tabId", "trailingIcon", "disabled"],
  _excluded2 = ["allowedMessageChannelIds", "associatedObjectId", "associatedObjectTypeId", "callingSource", "currentChannelId", "dropdownIsOpen", "hasCalling", "isKnownVisitor", "onSelectDropdownOption", "selectedChannelId", "setChannelDropdownIsOpen", "subjectId", "inboxId", "threadId"];
import I18n from 'I18n';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { useAsyncGenericChannelsWithCapabilities } from 'composer-data/channel-capabilities/public/hooks';
import { EMAIL_GENERIC_CHANNEL_ID, FB_MESSENGER_GENERIC_CHANNEL_ID, LIVE_CHAT_GENERIC_CHANNEL_ID, SMS_GENERIC_CHANNEL_ID, WHATSAPP_GENERIC_CHANNEL_ID } from 'conversations-message-history/common-message-format/constants/genericChannelIds';
import { isGenericChannelId } from 'conversations-thread-data/channel-configuration/public/types';
import { AccessLevelContextProvider } from 'customer-data-properties/accessLevel/AccessLevelContext';
import { memo, useCallback } from 'react';
import { ChannelLabel } from '../../../capability-editor/public/components';
import { ChannelDropdownOptions } from './ChannelDropdownOptions';
import { useSelector } from 'react-redux';
import { getIsUngatedForPersistedDrafts } from 'conversations-thread-data/auth/public/selectors';
import { usePersistentDraftsQuery } from '../../../common-reply/protected/hooks';
import { MessagesIcon, SocialFacebookMessengerIcon, EmailIcon, SocialWhatsappIcon, SmsIcon, DownCaratIcon } from 'foundations-assets';
import { Tab } from 'foundations-components/transitional/components/Tabs';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function getIconForGenericChannelId(genericChannelId) {
  switch (genericChannelId) {
    case FB_MESSENGER_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(SocialFacebookMessengerIcon, {});
    case EMAIL_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(EmailIcon, {});
    case WHATSAPP_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(SocialWhatsappIcon, {});
    case SMS_GENERIC_CHANNEL_ID:
      return /*#__PURE__*/_jsx(SmsIcon, {});
    case LIVE_CHAT_GENERIC_CHANNEL_ID:
    default:
      return /*#__PURE__*/_jsx(MessagesIcon, {});
  }
}
function ChannelTab(_ref) {
  let {
      selectedChannelId,
      tabId,
      trailingIcon = null,
      disabled
    } = _ref,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  const icon = getIconForGenericChannelId(selectedChannelId);
  return /*#__PURE__*/_jsx(Tab, Object.assign({
    tabId: tabId,
    "data-test-id": "HUBSPOT-dropdown-trigger",
    "data-tab-id": tabId,
    "data-channel-id": selectedChannelId,
    disabled: disabled,
    title: /*#__PURE__*/_jsxs(Flex, {
      gap: "100",
      alignItems: "center",
      "data-test-id": "channel-click",
      children: [icon, /*#__PURE__*/_jsx("span", {
        "data-test-id": "selected-channel-label",
        children: /*#__PURE__*/_jsx(ChannelLabel, {
          genericChannelId: selectedChannelId
        })
      }), trailingIcon]
    })
  }, rest));
}
ChannelTab.displayName = 'ChannelTab';
const ChannelSwitchDropdown = _ref2 => {
  let {
      allowedMessageChannelIds,
      associatedObjectId,
      associatedObjectTypeId,
      callingSource,
      currentChannelId,
      dropdownIsOpen,
      hasCalling,
      isKnownVisitor,
      onSelectDropdownOption,
      selectedChannelId,
      setChannelDropdownIsOpen,
      subjectId,
      inboxId,
      threadId
    } = _ref2,
    rest = _objectWithoutPropertiesLoose(_ref2, _excluded2);
  const isSelected = isGenericChannelId(currentChannelId);
  const closeDropdown = useCallback(() => {
    setChannelDropdownIsOpen(false);
  }, [setChannelDropdownIsOpen]);
  const ChannelTabWithTooltip = () => {
    const {
      loading,
      error
    } = useAsyncGenericChannelsWithCapabilities();
    const disableTab = Boolean(loading || error);
    return /*#__PURE__*/_jsx(Tooltip, {
      title: I18n.text('composer-ui.tabbed-editor.channel-switch-dropdown.tooltip-text'),
      disabled: dropdownIsOpen,
      children: /*#__PURE__*/_jsx(ChannelTab, Object.assign({
        selectedChannelId: selectedChannelId,
        trailingIcon: isSelected ? /*#__PURE__*/_jsx(DownCaratIcon, {
          size: "200"
        }) : null,
        disabled: disableTab
      }, rest))
    });
  };
  ChannelTabWithTooltip.displayName = 'ChannelTabWithTooltip';
  const isUngatedForPersistedDrafts = useSelector(getIsUngatedForPersistedDrafts);
  const {
    currentDrafts
  } = usePersistentDraftsQuery({
    idType: 'THREAD_ID',
    idNumber: Number(threadId),
    isUngatedForPersistedDrafts,
    fetchPolicy: 'cache-first'
  });
  return allowedMessageChannelIds.length === 1 && !hasCalling ? /*#__PURE__*/_jsx(ChannelTab, Object.assign({
    selectedChannelId: selectedChannelId
  }, rest)) : /*#__PURE__*/_jsx(AccessLevelContextProvider, {
    children: /*#__PURE__*/_jsx(Dropdown, {
      placement: "top",
      Button: ChannelTabWithTooltip,
      arrowSize: "small",
      animateOnToggle: true,
      closeOnMenuClick: false,
      open: dropdownIsOpen,
      caretRenderer: null,
      onOpenChange: closeDropdown,
      children: /*#__PURE__*/_jsx(ChannelDropdownOptions, {
        allowedMessageChannelIds: allowedMessageChannelIds,
        closeDropdown: closeDropdown,
        isSelected: isSelected,
        callingSource: callingSource,
        associatedObjectId: associatedObjectId,
        associatedObjectTypeId: associatedObjectTypeId,
        hasCalling: hasCalling,
        isKnownVisitor: isKnownVisitor,
        selectChannel: onSelectDropdownOption,
        subjectId: subjectId,
        inboxId: inboxId,
        threadId: threadId,
        currentDrafts: currentDrafts
      })
    })
  });
};
ChannelSwitchDropdown.displayName = 'ChannelSwitchDropdown';
export default /*#__PURE__*/memo(ChannelSwitchDropdown);